import React, { useState, useEffect, useCallback } from 'react';
import { Select, Spin, Empty, Typography, Button, Alert } from 'antd';
import { FolderOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Option } = Select;
const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const FolderSelector = ({ moduleType, onFolderSelect, onFoldersLoaded }) => {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [creatingFolders, setCreatingFolders] = useState(false);

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/folders/${moduleType}`);

      // Trust the backend — it is the single source of truth for the folder
      // range (driven by PROCUREMENT_FOLDER_START/END and SITE_FOLDER_START/END
      // in s3_service.py). No client-side range filter here, or it would
      // silently re-cap the list and hide newly added folders.
      let folderList = response.data.folders || [];

      // Numeric sort (ascending) so 2 doesn't sort after 10, etc.
      folderList.sort((a, b) => parseInt(a.name, 10) - parseInt(b.name, 10));

      setFolders(folderList);

      if (folderList.length === 0) {
        setError(
          `No folders returned for ${moduleType === 'site-petty-cash' ? 'Site' : 'Procurement'}. ` +
          `Check that the backend is running and the folder range is configured.`
        );
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setError('Failed to fetch folders. Please check if backend is running.');
    } finally {
      setLoading(false);
    }
  }, [moduleType]);

  const createFolders = async () => {
    setCreatingFolders(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/force-create-folders`);
      if (response.data.status === 'success') {
        await fetchFolders();
      } else {
        setError('Failed to create folders: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error creating folders:', error);
      setError('Failed to create folders. Please try again.');
    } finally {
      setCreatingFolders(false);
    }
  };

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Report the loaded folder list up to the parent (e.g. for the header
  // subtitle to show the real min–max range instead of a hardcoded one).
  useEffect(() => {
    if (onFoldersLoaded) onFoldersLoaded(folders);
  }, [folders, onFoldersLoaded]);

  const handleChange = (folderPath) => {
    const folder = folders.find(f => f.path === folderPath);
    setSelectedFolder(folder);
    onFolderSelect(folder);
  };

  // Clear selected folder when module changes
  useEffect(() => {
    setSelectedFolder(null);
  }, [moduleType]);

  return (
    <div className="folder-selector">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <FolderOutlined /> Select Folder
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchFolders}
          loading={loading}
          size="small"
        >
          Refresh
        </Button>
      </div>

      {error && (
        <Alert
          message={error}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            error.includes('No folders') && (
              <Button
                size="small"
                type="primary"
                onClick={createFolders}
                loading={creatingFolders}
              >
                Create Folders
              </Button>
            )
          }
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading folders...</Text>
          </div>
        </div>
      ) : folders.length > 0 ? (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Available Folders: {folders.length}
          </Text>
          <Select
            placeholder="Choose a folder number"
            style={{ width: '100%', maxWidth: 400 }}
            onChange={handleChange}
            value={selectedFolder?.path}
            size="large"
            showSearch
            optionFilterProp="children"
          >
            {folders.map(folder => (
              <Option key={folder.path} value={folder.path}>
                <FolderOutlined /> Folder {folder.name}
              </Option>
            ))}
          </Select>

          {selectedFolder && (
            <div style={{ marginTop: 12, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
              <Text type="secondary">Selected: </Text>
              <Text strong>Folder {selectedFolder.name}</Text>
            </div>
          )}
        </>
      ) : (
        <Empty
          description={
            <span>
              No folders available for {moduleType === 'site-petty-cash' ? 'Site Petty Cash' : 'Procurement PC'}
              <br />
              <Button
                type="link"
                onClick={createFolders}
                loading={creatingFolders}
              >
                Click here to create folders
              </Button>
            </span>
          }
        />
      )}
    </div>
  );
};

export default FolderSelector;